// Re-export shared utilities
export * from '@repo/utils/url'

// Keep web-specific API utilities
interface ApiErrorResponse {
  error?: { message?: string }
  message?: string
}

type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue }

const isRecord = (value: unknown): value is Record<string, JsonValue> =>
  typeof value === 'object' && value !== null

const extractErrorMessage = (payload: unknown): string | undefined => {
  if (!isRecord(payload)) {
    return undefined
  }

  const { error, message } = payload

  if (isRecord(error) && typeof error.message === 'string') {
    return error.message
  }

  if (typeof message === 'string') {
    return message
  }

  return undefined
}

export async function postJson<TResponse = JsonValue, TBody = JsonValue>(
  url: string,
  body: TBody
): Promise<TResponse> {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  })

  let payload: TResponse | ApiErrorResponse | undefined
  try {
    payload = await response.clone().json()
  } catch {
    payload = undefined
  }

  const errorMessage = extractErrorMessage(payload)

  if (!response.ok || errorMessage) {
    throw new Error(errorMessage ?? (response.statusText || 'Request failed'))
  }

  return payload as TResponse
}
